import React, { useState, useEffect, useMemo } from 'react';
import {
  FiPackage,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiFileText,
  FiSearch,
  FiFilter,
  FiX,
  FiRefreshCw,
  FiBox,
  FiTrendingUp,
  FiAlertCircle,
  FiDollarSign
} from 'react-icons/fi';
import Swal from 'sweetalert2';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/api';
import { fetchAllInvoices, purgeLocalInvoiceStorage } from '../../utils/zovcurInvoiceApi';
import {
  computeInvoiceTotals,
  convertFromBoxes,
  convertFromStrips,
  formatBoxQty,
  formatProductName,
  getProductPricing
} from '../../utils/zovcurInvoiceStore';

const MATERIAL_OPTIONS = [
  { name: 'Zovfolic', details: 'Folic Acid 5 mg' },
  {
    name: 'Ferrozov',
    details:
      'Ferrous Ascorbate (Iron 100 mg) + L-Methylfolate (0.50 mg) + Methylcobalamin (Vitamin B12 1500 mcg) + Biotin (0.60 mg) + Cholecalciferol (Vitamin D3 1000 IU) + Pyridoxal-5-Phosphate (Vitamin B6 Active Form 0.50 mg) + Zinc (5 mg) + Manganese (2 mg) + Copper (0.3 mg) + Iodine (35 mcg) + Molybdenum (15 mcg) + L-Leucine (5 mg) + L-Arginine (10 mg)'
  },
  { name: 'Calzov', details: 'Calcium Citrate 1000 mg + Vit D3 200 IU + Magnesium 100 mg + Zinc 4 mg' },
  { name: 'Zov-B12', details: 'Methylcobalamin 1500 mcg + L-methylfolate calcium 1 mg + Pyridoxal 5 phosphate 0.5 mg' },
  { name: 'Zovandro Q10', details: 'Coenzyme-Q10, L-Carnitine L-Tartrate, Astaxanthin, Lycopene With Zinc – Male Infertility' },
  { name: 'Zoviva', details: 'Myo-inositol 600 mg + Metformin 500 mg (SR)' },
  { name: 'Zovgest', details: 'Progesterone 200 mg' },
  { name: 'Dydozov', details: 'Dydrogesterone 30 mg' }
];

const formatMoneyShort = (value) => {
  const n = Number(value) || 0;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const today = () => new Date().toISOString().split('T')[0];

const parseSnoNumber = (value) => {
  const digits = String(value || '').trim().match(/(\d+)/);
  return digits ? Number.parseInt(digits[1], 10) : 0;
};

const formatSno = (num) => String(Math.max(1, num)).padStart(2, '0');

const getTransactionSno = (item) =>
  parseSnoNumber(item?.sno) ||
  parseSnoNumber(item?.reference) ||
  parseSnoNumber(item?.reason) ||
  0;

const getMaxSnoFromTransactions = (list = []) => {
  let max = 0;
  list.forEach((item) => {
    max = Math.max(max, getTransactionSno(item));
  });
  return max;
};

const sortBySnoAsc = (list = []) =>
  [...list].sort((a, b) => {
    const snoDiff = getTransactionSno(a) - getTransactionSno(b);
    if (snoDiff !== 0) return snoDiff;
    return String(a.materialName || '').localeCompare(String(b.materialName || ''));
  });

const createEmptyQtyEntry = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  quantity: '',
  unit: 'Box',
  date: today(),
  expiryDate: ''
});

const createEmptySampleEntry = (snoNum = 1, defaultUnit = 'Box') => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  sno: formatSno(snoNum),
  date: today(),
  customerName: '',
  unit: defaultUnit || 'Box',
  quantity: ''
});

const fromStoredSampleEntries = (transaction) => {
  if (Array.isArray(transaction?.sampleEntries) && transaction.sampleEntries.length > 0) {
    return [...transaction.sampleEntries]
      .map((row, index) => ({
        id: row.id || `${Date.now()}-${index}`,
        sno: row.sno || formatSno(index + 1),
        date: row.date ? new Date(row.date).toISOString().split('T')[0] : today(),
        customerName: row.customerName || '',
        unit: row.unit || transaction.unit || 'Box',
        quantity: String(row.quantity ?? '')
      }))
      .sort((a, b) => parseSnoNumber(a.sno) - parseSnoNumber(b.sno));
  }
  return [];
};

const toPersistSampleEntries = (entries = []) =>
  entries.map((row, index) => ({
    id: row.id || `${Date.now()}-${index}`,
    sno: row.sno || formatSno(index + 1),
    date: row.date || null,
    customerName: String(row.customerName || '').trim(),
    unit: row.unit || 'Box',
    quantity: Number(row.quantity) || 0
  }));

const getSampleTotal = (entries = []) =>
  entries.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);

const emptyFormState = (startNum = 1) => ({
  sno: formatSno(startNum),
  materialName: '',
  materialDetails: '',
  manufacturer: '',
  qtyEntries: [createEmptyQtyEntry()]
});

/** Build per-product order totals + invoice line details from saved invoices */
const resolveSalesmanName = (inv) => {
  const details =
    inv?.invoiceDetails && typeof inv.invoiceDetails === 'object'
      ? inv.invoiceDetails
      : {};
  const raw =
    details.salesmanName ??
    details.salesManName ??
    details.salesman ??
    inv?.salesmanName ??
    '';
  const name = String(raw).trim();
  return name || '—';
};

const buildInvoiceOrdersByProduct = (invoices = []) => {
  const map = {};
  invoices.forEach((inv) => {
    (inv.products || []).forEach((line) => {
      const key = formatProductName(line.product);
      if (!key) return;
      const qty = Number(line.qty) || 0;
      if (!map[key]) {
        map[key] = { totalOrdered: 0, lines: [] };
      }
      map[key].totalOrdered += qty;
      map[key].lines.push({
        id: `${inv.id || inv._id}-${line.id || line.product}-${line.batchNo || ''}`,
        invoiceId: inv.id || inv._id,
        invoiceNumber: inv.invoiceNumber,
        billDate: inv.invoiceDetails?.billDate || inv.createdAt,
        doctor: inv.customer?.doctor || '—',
        salesmanName: resolveSalesmanName(inv),
        address: inv.customer?.address || '',
        product: line.product,
        batchNo: line.batchNo || '—',
        qty,
        rate: line.rate,
        mrp: line.mrp
      });
    });
  });
  return map;
};

const StockInOut = () => {

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedOrderTransaction, setSelectedOrderTransaction] = useState(null);
  const [orderModalTab, setOrderModalTab] = useState('orders');
  const [sampleEntries, setSampleEntries] = useState([]);
  const [savingSamples, setSavingSamples] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [invoiceOrdersByProduct, setInvoiceOrdersByProduct] = useState({});
  const [allInvoices, setAllInvoices] = useState([]);
  const [formData, setFormData] = useState(() => emptyFormState(1));
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterManufacturer, setFilterManufacturer] = useState('all');
  const [filterUnit, setFilterUnit] = useState('all');
  const [filterBalance, setFilterBalance] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  const nextSnoNumber = useMemo(
    () => getMaxSnoFromTransactions(transactions) + 1,
    [transactions]
  );

  useEffect(() => {
    fetchTransactions();
    fetchManufacturers();
  }, []);

  const fetchInvoiceOrders = async () => {
    try {
      purgeLocalInvoiceStorage();
      const invoices = await fetchAllInvoices();
      setAllInvoices(invoices || []);
      setInvoiceOrdersByProduct(buildInvoiceOrdersByProduct(invoices));
    } catch (error) {
      console.error('Error fetching invoices for stock orders:', error);
      setAllInvoices([]);
      setInvoiceOrdersByProduct({});
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let allTransactions = [];
      try {
        const response = await axios.get(API_ENDPOINTS.getStockTransactions, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allTransactions = (response.data || []).filter((t) => t.type !== 'stock_out');
      } catch (error) {
        // Compatibility fallback: older backend with only stock-in routes.
        const fallbackResponse = await axios.get(API_ENDPOINTS.getStockIn, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allTransactions = (fallbackResponse.data || []).map((item) => ({
          _id: item._id,
          sno: item.sno || item.batchNumber || `STK-${item._id?.slice(-6) || Date.now()}`,
          type: 'stock_in',
          materialName: item.medicineName || '',
          materialDetails: item.notes || '',
          manufacturer: item.manufacturerName || '',
          quantity: item.quantity || 0,
          unit: item.unit || 'Box',
          date: item.purchaseDate || item.createdAt,
          expiryDate: item.expiryDate || null,
          reason: item.supplier || '',
          reference: item.invoiceNumber || '',
          notes: item.notes || ''
        }));
      }
      setTransactions(sortBySnoAsc(allTransactions));
      await fetchInvoiceOrders();
    } catch (error) {
      console.error('Error fetching transactions:', error);
      Swal.fire('Error', 'Failed to fetch transactions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchManufacturers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.getManufacturers, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManufacturers(response.data || []);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    }
  };

  const summaryByTransaction = useMemo(() => {
    const summary = {};
    transactions.forEach((transaction) => {
      const key = formatProductName(transaction.materialName);
      const invoiceData = invoiceOrdersByProduct[key] || { totalOrdered: 0, lines: [] };
      const totalOrderedStrips = invoiceData.totalOrdered || 0;
      const totalSamples = getSampleTotal(transaction.sampleEntries || []);
      const stockInQty = Number(transaction.quantity) || 0;
      const orderUnits = convertFromStrips(totalOrderedStrips, transaction.materialName);
      const orderBoxes = orderUnits.boxes;
      const balanceBoxes = Math.max(stockInQty - orderBoxes - totalSamples, 0);
      const balanceUnits = convertFromBoxes(balanceBoxes, transaction.materialName);
      summary[transaction._id] = {
        ordersCount: invoiceData.lines?.length || 0,
        totalOrdered: totalOrderedStrips,
        totalOrderedStrips,
        totalOrderedTablets: orderUnits.tablets,
        totalOrderedBoxes: orderBoxes,
        totalSamples,
        balance: balanceBoxes,
        balanceStrips: balanceUnits.strips,
        balanceTablets: balanceUnits.tablets,
        stripSize: orderUnits.stripSize,
        stripsPerBox: orderUnits.stripsPerBox,
        lines: invoiceData.lines || []
      };
    });
    return summary;
  }, [transactions, invoiceOrdersByProduct]);

  const productFilterOptions = useMemo(() => {
    const names = [
      ...new Set(transactions.map((t) => t.materialName).filter(Boolean))
    ].sort((a, b) => a.localeCompare(b));
    return names;
  }, [transactions]);

  const manufacturerFilterOptions = useMemo(() => {
    const names = [
      ...new Set(transactions.map((t) => t.manufacturer).filter(Boolean))
    ].sort((a, b) => a.localeCompare(b));
    return names;
  }, [transactions]);

  const unitFilterOptions = useMemo(() => {
    const units = [...new Set(transactions.map((t) => t.unit).filter(Boolean))].sort();
    return units;
  }, [transactions]);

  const displayedTransactions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const filtered = transactions.filter((t) => {
      const summary = summaryByTransaction[t._id] || { balance: 0, totalOrdered: 0 };
      const snoLabel = formatSno(getTransactionSno(t) || parseSnoNumber(t.sno));
      const haystack = [
        snoLabel,
        t.sno,
        t.materialName,
        t.materialDetails,
        t.manufacturer,
        t.unit,
        String(t.quantity)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (q && !haystack.includes(q)) return false;
      if (filterProduct !== 'all' && t.materialName !== filterProduct) return false;
      if (filterManufacturer !== 'all' && t.manufacturer !== filterManufacturer) return false;
      if (filterUnit !== 'all' && t.unit !== filterUnit) return false;

      if (filterBalance === 'available' && summary.balance <= 0) return false;
      if (filterBalance === 'low' && !(summary.balance > 0 && summary.balance <= 50)) return false;
      if (filterBalance === 'zero' && summary.balance !== 0) return false;

      const rowDate = t.date ? new Date(t.date) : null;
      if (dateFrom) {
        const from = new Date(dateFrom);
        if (!rowDate || Number.isNaN(rowDate.getTime()) || rowDate < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (!rowDate || Number.isNaN(rowDate.getTime()) || rowDate > to) return false;
      }

      return true;
    });

    return sortBySnoAsc(filtered);
  }, [
    transactions,
    summaryByTransaction,
    searchTerm,
    filterProduct,
    filterManufacturer,
    filterUnit,
    filterBalance,
    dateFrom,
    dateTo
  ]);

  const listStats = useMemo(() => {
    let stockBoxes = 0;
    let stockStrips = 0;
    let stockTablets = 0;
    let orderedBoxes = 0;
    let orderedStrips = 0;
    let orderedTablets = 0;
    let sampleBoxes = 0;
    let sampleStrips = 0;
    let sampleTablets = 0;
    let balanceBoxes = 0;
    let balanceStrips = 0;
    let balanceTablets = 0;
    let lowStock = 0;
    let totalCost = 0;

    displayedTransactions.forEach((t) => {
      const summary = summaryByTransaction[t._id] || {
        balance: 0,
        totalOrderedBoxes: 0,
        totalOrderedStrips: 0,
        totalOrderedTablets: 0,
        totalSamples: 0,
        balanceStrips: 0,
        balanceTablets: 0
      };
      const qty = Number(t.quantity) || 0;
      const stockUnits = convertFromBoxes(qty, t.materialName);
      const sampleUnits = convertFromBoxes(summary.totalSamples || 0, t.materialName);

      stockBoxes += qty;
      stockStrips += stockUnits.strips;
      stockTablets += stockUnits.tablets;

      orderedBoxes += summary.totalOrderedBoxes || 0;
      orderedStrips += summary.totalOrderedStrips || 0;
      orderedTablets += summary.totalOrderedTablets || 0;

      sampleBoxes += summary.totalSamples || 0;
      sampleStrips += sampleUnits.strips;
      sampleTablets += sampleUnits.tablets;

      balanceBoxes += summary.balance || 0;
      balanceStrips += summary.balanceStrips || 0;
      balanceTablets += summary.balanceTablets || 0;

      if (summary.balance > 0 && summary.balance <= 50) lowStock += 1;

      const pricing = getProductPricing(t.materialName);
      const rate = Number(pricing?.rate) || 0;
      // Doctor price is per strip — cost = stock strips × rate
      totalCost += stockUnits.strips * rate;
    });

    let invoiceCost = 0;
    let receivedCost = 0;
    allInvoices.forEach((inv) => {
      const totals = computeInvoiceTotals(inv.products || []);
      const amount = Number(totals.invoiceAmount) || 0;
      invoiceCost += amount;
      if ((inv.paymentStatus || 'Unpaid') === 'Paid') {
        receivedCost += amount;
      }
    });

    return {
      products: displayedTransactions.length,
      stockBoxes,
      stockStrips,
      stockTablets,
      orderedBoxes,
      orderedStrips,
      orderedTablets,
      sampleBoxes,
      sampleStrips,
      sampleTablets,
      balanceBoxes,
      balanceStrips,
      balanceTablets,
      lowStock,
      totalCost,
      invoiceCost,
      receivedCost
    };
  }, [displayedTransactions, summaryByTransaction, allInvoices]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm.trim()) count += 1;
    if (filterProduct !== 'all') count += 1;
    if (filterManufacturer !== 'all') count += 1;
    if (filterUnit !== 'all') count += 1;
    if (filterBalance !== 'all') count += 1;
    if (dateFrom) count += 1;
    if (dateTo) count += 1;
    return count;
  }, [
    searchTerm,
    filterProduct,
    filterManufacturer,
    filterUnit,
    filterBalance,
    dateFrom,
    dateTo
  ]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterProduct('all');
    setFilterManufacturer('all');
    setFilterUnit('all');
    setFilterBalance('all');
    setDateFrom('');
    setDateTo('');
  };

  const getDisplaySno = (transaction) => {
    const n = getTransactionSno(transaction);
    if (n > 0) return formatSno(n);
    return transaction.sno || '—';
  };

  const totalQty = useMemo(
    () => (formData.qtyEntries || []).reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
    [formData.qtyEntries]
  );

  const totalUnit = useMemo(() => {
    const units = [...new Set((formData.qtyEntries || []).map((row) => row.unit).filter(Boolean))];
    return units.length === 1 ? units[0] : units.length > 1 ? 'Mixed' : 'Box';
  }, [formData.qtyEntries]);

  const updateQtyEntry = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      qtyEntries: prev.qtyEntries.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    }));
  };

  const addQtyEntry = () => {
    setFormData((prev) => ({
      ...prev,
      qtyEntries: [...prev.qtyEntries, createEmptyQtyEntry()]
    }));
  };

  const removeQtyEntry = (id) => {
    setFormData((prev) => {
      if (prev.qtyEntries.length <= 1) return prev;
      return { ...prev, qtyEntries: prev.qtyEntries.filter((row) => row.id !== id) };
    });
  };

  const toPersistQtyEntries = (entries = []) =>
    entries.map((row) => ({
      id: row.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      quantity: Number(row.quantity) || 0,
      unit: row.unit || 'Box',
      date: row.date || null,
      expiryDate: row.expiryDate || null
    }));

  const fromStoredQtyEntries = (transaction) => {
    if (Array.isArray(transaction?.qtyEntries) && transaction.qtyEntries.length > 0) {
      return transaction.qtyEntries.map((row) => ({
        id: row.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        quantity: String(row.quantity ?? ''),
        unit: row.unit || transaction.unit || 'Box',
        date: row.date ? new Date(row.date).toISOString().split('T')[0] : today(),
        expiryDate: row.expiryDate ? new Date(row.expiryDate).toISOString().split('T')[0] : ''
      }));
    }
    return [
      {
        id: transaction?._id || createEmptyQtyEntry().id,
        quantity: String(transaction?.quantity ?? ''),
        unit: transaction?.unit || 'Box',
        date: transaction?.date ? new Date(transaction.date).toISOString().split('T')[0] : today(),
        expiryDate: transaction?.expiryDate
          ? new Date(transaction.expiryDate).toISOString().split('T')[0]
          : ''
      }
    ];
  };

  const normalizeName = (name) => String(name || '').trim().toUpperCase();

  const findExistingProductStock = (materialName, excludeId = null) => {
    const key = normalizeName(materialName);
    const matches = transactions.filter((item) => {
      if (excludeId && item._id === excludeId) return false;
      return normalizeName(item.materialName) === key;
    });
    if (!matches.length) return null;
    return [...matches].sort((a, b) => getTransactionSno(a) - getTransactionSno(b))[0];
  };

  const existingProductStock = useMemo(() => {
    if (!formData.materialName || selectedTransaction) return null;
    return findExistingProductStock(formData.materialName);
  }, [formData.materialName, selectedTransaction, transactions]);

  useEffect(() => {
    if (!showModal || selectedTransaction) return;
    if (existingProductStock) {
      setFormData((prev) => {
        // Only hydrate once per product — never overwrite rows the user just added
        if (prev._loadedFor === existingProductStock._id) {
          return prev.sno === existingProductStock.sno
            ? prev
            : { ...prev, sno: existingProductStock.sno };
        }
        return {
          ...prev,
          sno: existingProductStock.sno,
          manufacturer: prev.manufacturer || existingProductStock.manufacturer || '',
          materialDetails: prev.materialDetails || existingProductStock.materialDetails || '',
          qtyEntries: fromStoredQtyEntries(existingProductStock),
          _loadedFor: existingProductStock._id
        };
      });
    } else if (formData.materialName) {
      const next = formatSno(nextSnoNumber);
      setFormData((prev) => {
        if (prev._loadedFor) {
          return {
            ...prev,
            sno: next,
            qtyEntries: [createEmptyQtyEntry()],
            _loadedFor: null
          };
        }
        return prev.sno === next ? prev : { ...prev, sno: next };
      });
    }
  }, [existingProductStock, showModal, selectedTransaction, formData.materialName, nextSnoNumber]);

  const saveStockPayload = async (token, payload, existingId = null) => {
    if (existingId) {
      const response = await axios.put(
        API_ENDPOINTS.updateStockTransaction(existingId),
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    }

    const response = await axios.post(API_ENDPOINTS.createStockTransaction, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.materialName || !formData.manufacturer) {
        Swal.fire('Error', 'Please select material and manufacturer', 'error');
        return;
      }
      if (!formData.qtyEntries.length) {
        Swal.fire('Error', 'Please add at least one quantity entry', 'error');
        return;
      }
      const invalidRow = formData.qtyEntries.find(
        (row) => !row.quantity || Number(row.quantity) <= 0 || !row.date
      );
      if (invalidRow) {
        Swal.fire('Error', 'Each qty card needs Quantity (>0), Unit and Date', 'error');
        return;
      }
      if (totalUnit === 'Mixed') {
        Swal.fire('Error', 'Please use the same unit for all quantity cards', 'error');
        return;
      }

      const token = localStorage.getItem('token');
      const latestRow = formData.qtyEntries[formData.qtyEntries.length - 1];
      const persistedEntries = toPersistQtyEntries(formData.qtyEntries);
      const addedQty = totalQty;

      // Edit existing row: replace qty cards and set total = sum
      if (selectedTransaction?._id) {
        await saveStockPayload(
          token,
          {
            sno: selectedTransaction.sno || formData.sno,
            type: 'stock_in',
            materialName: formData.materialName,
            materialDetails: formData.materialDetails,
            manufacturer: formData.manufacturer,
            quantity: addedQty,
            unit: latestRow.unit,
            date: latestRow.date,
            expiryDate: latestRow.expiryDate || null,
            qtyEntries: persistedEntries,
            reason: formData.sno,
            reference: formData.sno,
            notes: formData.materialDetails
          },
          selectedTransaction._id
        );
        Swal.fire('Success', 'Stock updated successfully', 'success');
      } else {
        const existing = findExistingProductStock(formData.materialName);
        if (existing?._id) {
          // Keep previous cards + append only newly typed cards if form already includes existing
          // Form shows all cards when product selected, so persist form cards as full list
          const newTotal = persistedEntries.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
          await saveStockPayload(
            token,
            {
              sno: existing.sno,
              type: 'stock_in',
              materialName: formData.materialName,
              materialDetails: formData.materialDetails || existing.materialDetails || '',
              manufacturer: formData.manufacturer || existing.manufacturer,
              quantity: newTotal,
              unit: latestRow.unit || existing.unit || 'Box',
              date: latestRow.date,
              expiryDate: latestRow.expiryDate || existing.expiryDate || null,
              qtyEntries: persistedEntries,
              reason: existing.reason || existing.sno,
              reference: existing.reference || existing.sno,
              notes: formData.materialDetails || existing.notes || ''
            },
            existing._id
          );
          Swal.fire(
            'Success',
            `Stock updated for ${formData.materialName}. Total: ${newTotal} ${latestRow.unit} (${persistedEntries.length} entries)`,
            'success'
          );
        } else {
          await saveStockPayload(token, {
            sno: formData.sno,
            type: 'stock_in',
            materialName: formData.materialName,
            materialDetails: formData.materialDetails,
            manufacturer: formData.manufacturer,
            quantity: addedQty,
            unit: latestRow.unit,
            date: latestRow.date,
            expiryDate: latestRow.expiryDate || null,
            qtyEntries: persistedEntries,
            reason: formData.sno,
            reference: formData.sno,
            notes: formData.materialDetails
          });
          Swal.fire(
            'Success',
            `Stock added successfully (${persistedEntries.length} quantity entries)`,
            'success'
          );
        }
      }

      setShowModal(false);
      setSelectedTransaction(null);
      setFormData(emptyFormState(1));
      fetchTransactions();
    } catch (error) {
      console.error('Error saving transaction:', error);
      const message =
        error.response?.data?.error ||
        error.message ||
        'Failed to save transaction';
      Swal.fire('Error', message, 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        try {
          await axios.delete(API_ENDPOINTS.deleteStockTransaction(id), {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          await axios.delete(API_ENDPOINTS.deleteStockIn(id), {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        Swal.fire('Deleted', 'Transaction has been deleted', 'success');
        fetchTransactions();
      } catch (error) {
        console.error('Error deleting transaction:', error);
        Swal.fire('Error', 'Failed to delete transaction', 'error');
      }
    }
  };

  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setFormData({
      sno: formatSno(getTransactionSno(transaction) || nextSnoNumber),
      materialName: transaction.materialName,
      materialDetails: transaction.materialDetails || '',
      manufacturer: transaction.manufacturer || '',
      qtyEntries: fromStoredQtyEntries(transaction),
      _loadedFor: transaction._id
    });
    setShowModal(true);
  };

  const handleOpenOrderModal = (transaction) => {
    setSelectedOrderTransaction(transaction);
    setOrderModalTab('orders');
    setSampleEntries(fromStoredSampleEntries(transaction));
    setShowOrderModal(true);
    // Refresh invoices so salesman / order lines are up to date
    fetchInvoiceOrders();
  };

  const selectedInvoiceOrders = useMemo(() => {
    if (!selectedOrderTransaction) return { totalOrdered: 0, lines: [] };
    const key = formatProductName(selectedOrderTransaction.materialName);
    const base = invoiceOrdersByProduct[key] || { totalOrdered: 0, lines: [] };
    // Always resolve salesman from live invoice list (same source as Orders page)
    const lines = (base.lines || []).map((line) => {
      const inv = allInvoices.find(
        (item) =>
          String(item.id || item._id) === String(line.invoiceId) ||
          item.invoiceNumber === line.invoiceNumber
      );
      return {
        ...line,
        salesmanName: inv ? resolveSalesmanName(inv) : line.salesmanName || '—'
      };
    });
    return { ...base, lines };
  }, [selectedOrderTransaction, invoiceOrdersByProduct, allInvoices]);

  const sampleTotalQty = useMemo(() => getSampleTotal(sampleEntries), [sampleEntries]);

  const orderModalBalance = useMemo(() => {
    const stockIn = Number(selectedOrderTransaction?.quantity) || 0;
    const orderedStrips = selectedInvoiceOrders.totalOrdered || 0;
    const orderBoxes = convertFromStrips(
      orderedStrips,
      selectedOrderTransaction?.materialName
    ).boxes;
    return Math.max(stockIn - orderBoxes - sampleTotalQty, 0);
  }, [selectedOrderTransaction, selectedInvoiceOrders, sampleTotalQty]);

  const orderModalOrderUnits = useMemo(
    () =>
      convertFromStrips(
        selectedInvoiceOrders.totalOrdered || 0,
        selectedOrderTransaction?.materialName
      ),
    [selectedInvoiceOrders, selectedOrderTransaction]
  );

  const orderModalBalanceUnits = useMemo(
    () => convertFromBoxes(orderModalBalance, selectedOrderTransaction?.materialName),
    [orderModalBalance, selectedOrderTransaction]
  );

  const addSampleEntry = () => {
    setSampleEntries((prev) => {
      const nextSno = prev.length + 1;
      return [
        ...prev,
        createEmptySampleEntry(nextSno, selectedOrderTransaction?.unit || 'Box')
      ];
    });
  };

  const updateSampleEntry = (id, field, value) => {
    setSampleEntries((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const removeSampleEntry = (id) => {
    setSampleEntries((prev) => {
      const next = prev.filter((row) => row.id !== id);
      return next.map((row, index) => ({ ...row, sno: formatSno(index + 1) }));
    });
  };

  const handleSaveSamples = async () => {
    if (!selectedOrderTransaction?._id) {
      Swal.fire('Error', 'Stock record not found', 'error');
      return;
    }

    const invalid = sampleEntries.find(
      (row) =>
        !String(row.customerName || '').trim() ||
        !row.date ||
        !row.quantity ||
        Number(row.quantity) <= 0
    );
    if (invalid) {
      Swal.fire('Error', 'Each sample needs Date, Customer name, Unit and Quantity (> 0)', 'error');
      return;
    }

    setSavingSamples(true);
    try {
      const token = localStorage.getItem('token');
      const persisted = toPersistSampleEntries(sampleEntries);
      const response = await axios.put(
        API_ENDPOINTS.updateStockTransaction(selectedOrderTransaction._id),
        { sampleEntries: persisted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = response.data || {
        ...selectedOrderTransaction,
        sampleEntries: persisted
      };
      setSelectedOrderTransaction(updated);
      setSampleEntries(fromStoredSampleEntries(updated));
      setTransactions((prev) =>
        sortBySnoAsc(
          prev.map((item) => (item._id === updated._id ? { ...item, ...updated } : item))
        )
      );
      Swal.fire('Success', 'Sample entries saved', 'success');
    } catch (error) {
      console.error('Error saving samples:', error);
      Swal.fire(
        'Error',
        error.response?.data?.error || error.message || 'Failed to save samples',
        'error'
      );
    } finally {
      setSavingSamples(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-slate-100 via-slate-50 to-teal-50/40 -m-4 md:-m-6 p-4 md:p-6">
      <div className="w-full max-w-none">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1a365d]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#1a365d] mb-2">
              <FiBox /> Inventory
            </div>
            <h1 className="text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#1a365d] text-white shadow-md">
                <FiPackage />
              </span>
              Stock In
            </h1>
            <p className="text-slate-500 mt-1">
              Track stock receipts, invoice orders, and available balance — sorted by SNO
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fetchTransactions()}
              className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2 shadow-sm"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedTransaction(null);
                setFormData(emptyFormState(nextSnoNumber));
                setShowModal(true);
              }}
              className="bg-[#1a365d] text-white px-4 py-2 rounded-xl hover:bg-[#153050] flex items-center gap-2 shadow-md"
            >
              <FiPlus /> New Stock In
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Products</p>
            <p className="mt-1 text-2xl font-bold text-[#1a365d]">{listStats.products}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
              <FiTrendingUp /> Stock In
            </p>
            <p className="mt-1 text-2xl font-bold text-teal-700">
              {formatBoxQty(listStats.stockBoxes)}{' '}
              <span className="text-base font-semibold">box</span>
            </p>
            <p className="text-xs text-slate-600 mt-1">
              {listStats.stockStrips.toLocaleString('en-IN')} strips
            </p>
            <p className="text-xs text-slate-500">
              {listStats.stockTablets.toLocaleString('en-IN')} tablets
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ordered</p>
            <p className="mt-1 text-2xl font-bold text-amber-700">
              {formatBoxQty(listStats.orderedBoxes)}{' '}
              <span className="text-base font-semibold">box</span>
            </p>
            <p className="text-xs text-slate-600 mt-1">
              {listStats.orderedStrips.toLocaleString('en-IN')} strips
            </p>
            <p className="text-xs text-slate-500">
              {listStats.orderedTablets.toLocaleString('en-IN')} tablets
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sample</p>
            <p className="mt-1 text-2xl font-bold text-violet-700">
              {formatBoxQty(listStats.sampleBoxes)}{' '}
              <span className="text-base font-semibold">box</span>
            </p>
            <p className="text-xs text-slate-600 mt-1">
              {listStats.sampleStrips.toLocaleString('en-IN')} strips
            </p>
            <p className="text-xs text-slate-500">
              {listStats.sampleTablets.toLocaleString('en-IN')} tablets
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
              <FiAlertCircle /> Balance / Low
            </p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">
              {formatBoxQty(listStats.balanceBoxes)}{' '}
              <span className="text-base font-semibold">box</span>
              <span className="ml-2 text-sm font-semibold text-rose-600">
                ({listStats.lowStock} low)
              </span>
            </p>
            <p className="text-xs text-slate-600 mt-1">
              {listStats.balanceStrips.toLocaleString('en-IN')} strips
            </p>
            <p className="text-xs text-slate-500">
              {listStats.balanceTablets.toLocaleString('en-IN')} tablets
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
              <FiDollarSign /> Total Cost
            </p>
            <p className="mt-1 text-2xl font-bold text-[#1a365d]">
              {formatMoneyShort(listStats.totalCost)}
            </p>
            <p className="text-xs text-slate-400 mt-1">Stock strips × doctor price (per strip)</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Invoice Cost
            </p>
            <p className="mt-1 text-2xl font-bold text-amber-700">
              {formatMoneyShort(listStats.invoiceCost)}
            </p>
            <p className="text-xs text-slate-400 mt-1">All invoice amounts</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Received Cost
            </p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">
              {formatMoneyShort(listStats.receivedCost)}
            </p>
            <p className="text-xs text-slate-400 mt-1">Paid invoice amounts</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-5">
          <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 border-b border-slate-100">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search SNO, product, manufacturer..."
                className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((prev) => !prev)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium"
            >
              <FiFilter />
              Filters
              {activeFilterCount > 0 ? (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-teal-600 px-1.5 text-xs text-white">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
            {activeFilterCount > 0 ? (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-rose-600 hover:bg-rose-50 text-sm font-medium"
              >
                <FiX /> Clear
              </button>
            ) : null}
          </div>

          {showFilters ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 p-4 bg-slate-50/80">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Product</label>
                <select
                  value={filterProduct}
                  onChange={(e) => setFilterProduct(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All products</option>
                  {productFilterOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Manufacturer</label>
                <select
                  value={filterManufacturer}
                  onChange={(e) => setFilterManufacturer(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All manufacturers</option>
                  {manufacturerFilterOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Unit</label>
                <select
                  value={filterUnit}
                  onChange={(e) => setFilterUnit(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All units</option>
                  {unitFilterOptions.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Balance</label>
                <select
                  value={filterBalance}
                  onChange={(e) => setFilterBalance(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All balances</option>
                  <option value="available">Available (&gt; 0)</option>
                  <option value="low">Low (1–50)</option>
                  <option value="zero">Zero balance</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Date from</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Date to</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
            <p className="text-sm text-slate-600">
              Showing <strong className="text-[#1a365d]">{displayedTransactions.length}</strong> of{' '}
              <strong>{transactions.length}</strong> products · SNO ascending
            </p>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-9 w-9 border-b-2 border-[#1a365d]" />
              <p className="mt-3 text-sm text-slate-500">Loading stock...</p>
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <table className="w-full min-w-[1400px] table-fixed border-collapse">
                <colgroup>
                  <col className="w-[70px]" />
                  <col className="w-[110px]" />
                  <col className="w-[180px]" />
                  <col className="w-[150px]" />
                  <col className="w-[110px]" />
                  <col className="w-[130px]" />
                  <col className="w-[90px]" />
                  <col className="w-[100px]" />
                  <col className="w-[110px]" />
                  <col className="w-[120px]" />
                  <col className="w-[120px]" />
                </colgroup>
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#1a365d] text-white">
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      SNO
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Date
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Product
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Manufacturer
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Stock In
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Orders
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Sample
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Strips
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Tablets
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Balance
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedTransactions.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center py-16 text-slate-500">
                        {transactions.length === 0
                          ? 'No stock records yet. Create a new stock in entry.'
                          : 'No records match your search or filters.'}
                      </td>
                    </tr>
                  ) : (
                    displayedTransactions.map((transaction, index) => {
                      const summary = summaryByTransaction[transaction._id] || {
                        balance: 0,
                        totalOrdered: 0,
                        totalOrderedStrips: 0,
                        totalOrderedTablets: 0,
                        totalOrderedBoxes: 0,
                        totalSamples: 0,
                        balanceStrips: 0,
                        balanceTablets: 0
                      };
                      const balance = summary.balance || 0;
                      const balanceTone =
                        balance <= 0
                          ? 'bg-rose-50 text-rose-700'
                          : balance <= 50
                            ? 'bg-amber-50 text-amber-800'
                            : 'bg-emerald-50 text-emerald-800';
                      return (
                        <tr
                          key={transaction._id || transaction.id}
                          className={`border-b border-slate-100 hover:bg-teal-50/40 transition-colors ${
                            index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                          }`}
                        >
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className="inline-flex items-center justify-center min-w-[42px] rounded-lg bg-[#1a365d] px-2 py-1 text-xs font-bold text-white">
                              {getDisplaySno(transaction)}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">
                            {formatDate(transaction.date)}
                          </td>
                          <td className="px-3 py-3 min-w-0">
                            <p className="text-sm font-semibold uppercase text-slate-900 truncate">
                              {formatProductName(transaction.materialName)}
                            </p>
                            {transaction.materialDetails ? (
                              <p className="text-xs text-slate-500 truncate mt-0.5">
                                {transaction.materialDetails}
                              </p>
                            ) : null}
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-700 truncate whitespace-nowrap">
                            {transaction.manufacturer || '—'}
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-700 whitespace-nowrap">
                            <span className="font-semibold text-[#1a365d]">
                              {transaction.quantity} {transaction.unit}
                            </span>
                            {Array.isArray(transaction.qtyEntries) &&
                            transaction.qtyEntries.length > 0 ? (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {transaction.qtyEntries.length}{' '}
                                {transaction.qtyEntries.length === 1 ? 'entry' : 'entries'}
                              </p>
                            ) : null}
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-700 whitespace-nowrap">
                            <p className="font-semibold text-teal-800">
                              {summary.totalOrderedStrips || 0} strips
                            </p>
                            <p className="text-xs text-slate-600 mt-0.5">
                              {(summary.totalOrderedTablets || 0).toLocaleString('en-IN')} tablets
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {formatBoxQty(summary.totalOrderedBoxes || 0)} box
                            </p>
                          </td>
                          <td className="px-3 py-3 text-sm whitespace-nowrap">
                            <span className="font-semibold text-amber-700">
                              {summary.totalSamples || 0}
                            </span>
                            {Array.isArray(transaction.sampleEntries) &&
                            transaction.sampleEntries.length > 0 ? (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {transaction.sampleEntries.length}{' '}
                                {transaction.sampleEntries.length === 1 ? 'entry' : 'entries'}
                              </p>
                            ) : null}
                          </td>
                          <td className="px-3 py-3 text-sm whitespace-nowrap">
                            <span className="font-semibold text-[#0e8a9a]">
                              {(summary.balanceStrips || 0).toLocaleString('en-IN')}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm whitespace-nowrap">
                            <span className="font-semibold text-slate-800">
                              {(summary.balanceTablets || 0).toLocaleString('en-IN')}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${balanceTone}`}
                            >
                              {formatBoxQty(balance)} {transaction.unit}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleEdit(transaction)}
                                className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50"
                                title="Edit"
                              >
                                <FiEdit />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleOpenOrderModal(transaction)}
                                className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"
                                title="Orders & Samples"
                              >
                                <FiFileText />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(transaction._id)}
                                className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"
                                title="Delete"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <h2 className="text-2xl font-bold mb-1 text-[#1a365d]">
              {selectedTransaction ? 'Edit Stock In' : 'New Stock In'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Select product, then add stock quantity rows in the table
            </p>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SNO</label>
                    <input
                      type="text"
                      value={formData.sno}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500"
                      readOnly
                      title="Auto-generated SNO"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Material Name</label>
                    <select
                      value={formData.materialName}
                      onChange={(e) => {
                        const selected = MATERIAL_OPTIONS.find((item) => item.name === e.target.value);
                        setFormData((prev) => ({
                          ...prev,
                          materialName: e.target.value,
                          materialDetails: selected?.details || '',
                          _loadedFor: null
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select product</option>
                      {MATERIAL_OPTIONS.map((item) => (
                        <option key={item.name} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                    <select
                      value={formData.manufacturer}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, manufacturer: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select manufacturer</option>
                      {manufacturers.map((manufacturer) => (
                        <option key={manufacturer._id} value={manufacturer.companyName}>
                          {manufacturer.companyName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData.materialDetails ? (
                  <p className="text-xs text-gray-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    {formData.materialDetails}
                  </p>
                ) : null}

                {existingProductStock ? (
                  <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
                    <strong>{formData.materialName}</strong> already exists (SNO {existingProductStock.sno}).
                    Showing <strong>{formData.qtyEntries.length}</strong> quantity{' '}
                    {formData.qtyEntries.length === 1 ? 'entry' : 'entries'}. Use <strong>Add Qty</strong> to add more
                    — all entries stay on this same product row.
                  </div>
                ) : null}

                <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/70">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800">Stock Quantity Table</h3>
                    <button
                      type="button"
                      onClick={addQtyEntry}
                      className="bg-teal-600 text-white px-3 py-1.5 rounded-lg hover:bg-teal-700 flex items-center gap-1 text-sm"
                    >
                      <FiPlus /> Add Qty
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-[#1a365d] text-white">
                          <th className="px-3 py-2 text-left font-semibold w-20">Qty #</th>
                          <th className="px-3 py-2 text-left font-semibold">Quantity</th>
                          <th className="px-3 py-2 text-left font-semibold">Unit</th>
                          <th className="px-3 py-2 text-left font-semibold">Date</th>
                          <th className="px-3 py-2 text-left font-semibold">Expiry Date</th>
                          <th className="px-3 py-2 text-center font-semibold w-14">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.qtyEntries.map((row, index) => (
                          <tr
                            key={row.id}
                            className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                          >
                            <td className="px-3 py-2 font-semibold text-slate-600 whitespace-nowrap">
                              Qty #{index + 1}
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={row.quantity}
                                onChange={(e) => updateQtyEntry(row.id, 'quantity', e.target.value)}
                                className="w-full min-w-[90px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <select
                                value={row.unit}
                                onChange={(e) => updateQtyEntry(row.id, 'unit', e.target.value)}
                                className="w-full min-w-[100px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="Box">Box</option>
                                <option value="Strips">Strips</option>
                                <option value="units">Units</option>
                                <option value="bottles">Bottles</option>
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="date"
                                value={row.date}
                                onChange={(e) => updateQtyEntry(row.id, 'date', e.target.value)}
                                className="w-full min-w-[130px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="date"
                                value={row.expiryDate}
                                onChange={(e) => updateQtyEntry(row.id, 'expiryDate', e.target.value)}
                                className="w-full min-w-[130px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              {formData.qtyEntries.length > 1 ? (
                                <button
                                  type="button"
                                  onClick={() => removeQtyEntry(row.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Remove"
                                >
                                  <FiTrash2 />
                                </button>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-[#1a365d] text-white rounded-lg px-4 py-3">
                    <span className="text-sm font-medium">
                      Total Quantity ({formData.qtyEntries.length}{' '}
                      {formData.qtyEntries.length === 1 ? 'entry' : 'entries'})
                    </span>
                    <span className="text-lg font-bold">
                      {totalQty} {totalUnit}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedTransaction(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {selectedTransaction ? 'Update' : 'Create'} Stock In
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-2xl font-bold text-[#1a365d]">Invoice Orders</h2>
                <p className="text-sm text-gray-600">
                  {selectedOrderTransaction?.materialName} — orders & customer samples
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowOrderModal(false);
                  setSelectedOrderTransaction(null);
                  setOrderModalTab('orders');
                }}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
                title="Close"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 uppercase">Stock In</p>
                <p className="text-lg font-bold text-slate-800">
                  {selectedOrderTransaction?.quantity || 0} {selectedOrderTransaction?.unit}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 uppercase">Orders</p>
                <p className="text-lg font-bold text-teal-700">
                  {orderModalOrderUnits.strips} strips
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {orderModalOrderUnits.tablets.toLocaleString('en-IN')} tablets ·{' '}
                  {formatBoxQty(orderModalOrderUnits.boxes)} box
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 uppercase">Samples</p>
                <p className="text-lg font-bold text-amber-700">
                  {sampleTotalQty} {selectedOrderTransaction?.unit || 'Box'}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 uppercase">Balance</p>
                <p className="text-lg font-bold text-[#1a365d]">
                  {formatBoxQty(orderModalBalance)} {selectedOrderTransaction?.unit}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {orderModalBalanceUnits.strips.toLocaleString('en-IN')} strips ·{' '}
                  {orderModalBalanceUnits.tablets.toLocaleString('en-IN')} tablets
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4 border-b border-slate-200 pb-1">
              <button
                type="button"
                onClick={() => setOrderModalTab('orders')}
                className={`px-4 py-2 rounded-t-lg text-sm font-semibold whitespace-nowrap ${
                  orderModalTab === 'orders'
                    ? 'bg-[#1a365d] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Orders
              </button>
              <button
                type="button"
                onClick={() => setOrderModalTab('sample')}
                className={`px-4 py-2 rounded-t-lg text-sm font-semibold whitespace-nowrap ${
                  orderModalTab === 'sample'
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Sample
              </button>
            </div>

            {orderModalTab === 'orders' ? (
              <>
                <h3 className="font-semibold mb-2 text-[#1a365d]">Invoice line details</h3>
                <div className="border rounded-xl overflow-x-auto max-h-72">
                  <table className="w-full min-w-[800px] text-sm">
                    <thead className="bg-[#1a365d] text-white sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Invoice</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Date</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Doctor</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap min-w-[120px]">
                          Salesman Name
                        </th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Batch</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Qty (strips)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedInvoiceOrders.lines || []).length === 0 ? (
                        <tr>
                          <td className="px-3 py-6 text-center text-gray-500" colSpan="6">
                            No invoice orders found for this product
                          </td>
                        </tr>
                      ) : (
                        selectedInvoiceOrders.lines.map((line) => (
                          <tr key={line.id} className="border-t hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium whitespace-nowrap">
                              {line.invoiceNumber}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatDate(line.billDate)}
                            </td>
                            <td className="px-3 py-2">{line.doctor}</td>
                            <td className="px-3 py-2 font-medium text-[#0e3d52] whitespace-nowrap">
                              {line.salesmanName && line.salesmanName !== '—'
                                ? String(line.salesmanName).trim()
                                : '—'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{line.batchNo}</td>
                            <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                              {line.qty}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm mt-3 text-gray-700">
                  Total Ordered from Invoices:{' '}
                  <span className="font-semibold">
                    {orderModalOrderUnits.strips} strips ·{' '}
                    {orderModalOrderUnits.tablets.toLocaleString('en-IN')} tablets ·{' '}
                    {formatBoxQty(orderModalOrderUnits.boxes)} box
                  </span>
                </p>
              </>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <h3 className="font-semibold text-[#1a365d]">Customer Sample Entries</h3>
                    <p className="text-xs text-slate-500">
                      Give samples to customers and save — SNO stays in order
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addSampleEntry}
                    className="inline-flex items-center gap-1.5 bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium"
                  >
                    <FiPlus /> Add Sample
                  </button>
                </div>

                <div className="border rounded-xl overflow-x-auto">
                  <table className="w-full min-w-[780px] text-sm table-fixed border-collapse">
                    <colgroup>
                      <col className="w-[70px]" />
                      <col className="w-[140px]" />
                      <col />
                      <col className="w-[120px]" />
                      <col className="w-[110px]" />
                      <col className="w-[70px]" />
                    </colgroup>
                    <thead className="bg-[#1a365d] text-white sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left whitespace-nowrap">SNO</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Date</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Customer Name</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Unit</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Quantity</th>
                        <th className="px-3 py-2 text-center whitespace-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sampleEntries.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-3 py-8 text-center text-slate-500">
                            No sample entries yet. Click <strong>Add Sample</strong> to create one.
                          </td>
                        </tr>
                      ) : (
                        sampleEntries.map((row, index) => (
                          <tr
                            key={row.id}
                            className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                          >
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className="inline-flex min-w-[36px] justify-center rounded-md bg-[#1a365d] px-2 py-1 text-xs font-bold text-white">
                                {row.sno || formatSno(index + 1)}
                              </span>
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="date"
                                value={row.date}
                                onChange={(e) => updateSampleEntry(row.id, 'date', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                                required
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="text"
                                value={row.customerName}
                                onChange={(e) =>
                                  updateSampleEntry(row.id, 'customerName', e.target.value)
                                }
                                placeholder="Customer / doctor name"
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                                required
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <select
                                value={row.unit}
                                onChange={(e) => updateSampleEntry(row.id, 'unit', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                              >
                                <option value="Box">Box</option>
                                <option value="Strips">Strips</option>
                                <option value="units">Units</option>
                                <option value="bottles">Bottles</option>
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={row.quantity}
                                onChange={(e) =>
                                  updateSampleEntry(row.id, 'quantity', e.target.value)
                                }
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                                required
                              />
                            </td>
                            <td className="px-2 py-1.5 text-center whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => removeSampleEntry(row.id)}
                                className="p-1.5 text-rose-500 hover:text-rose-700"
                                title="Remove"
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-gray-700">
                    Total Samples:{' '}
                    <span className="font-semibold">
                      {sampleTotalQty} {selectedOrderTransaction?.unit || 'Box'}
                    </span>{' '}
                    ({sampleEntries.length}{' '}
                    {sampleEntries.length === 1 ? 'entry' : 'entries'})
                  </p>
                  <button
                    type="button"
                    onClick={handleSaveSamples}
                    disabled={savingSamples}
                    className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 text-sm font-medium"
                  >
                    {savingSamples ? 'Saving…' : 'Save Samples'}
                  </button>
                </div>
              </>
            )}

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowOrderModal(false);
                  setSelectedOrderTransaction(null);
                  setOrderModalTab('orders');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockInOut;

